/**
 * GuestsManagement - Manage accommodation guests
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, Plus, Edit2, Search, Phone, Mail, Building2,
  CreditCard, User, Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GUEST_PURPOSES = [
  { value: 'guest', label: 'Guest' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'visitor', label: 'Visitor' },
  { value: 'student', label: 'Student' },
  { value: 'staff', label: 'Staff' },
  { value: 'vip', label: 'VIP' }
];

const ID_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'national_id', label: 'National ID' },
  { value: 'driver_license', label: 'Driver License' },
  { value: 'employee_id', label: 'Employee ID' },
  { value: 'student_id', label: 'Student ID' }
];

const GuestsManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch guests
  const { data: guests = [], isLoading } = useQuery({
    queryKey: ['accommodation-guests', searchTerm],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/accommodation/guests`;
      if (searchTerm) url += `?search=${encodeURIComponent(searchTerm)}`;
      const res = await fetch(url, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create guest
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/guests`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create guest');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-guests']);
      setShowAddDialog(false);
      toast.success('Guest profile created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update guest
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/guests/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update guest');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['accommodation-guests']);
      setEditingGuest(null);
      toast.success('Guest profile updated');
    },
    onError: (error) => toast.error(error.message)
  });

  const GuestForm = ({ guest, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      full_name: guest?.full_name || '',
      email: guest?.email || '',
      phone: guest?.phone || '',
      id_type: guest?.id_type || '',
      id_number: guest?.id_number || '',
      company: guest?.company || '',
      purpose: guest?.purpose || 'guest',
      emergency_contact: guest?.emergency_contact || '',
      emergency_phone: guest?.emergency_phone || '',
      notes: guest?.notes || ''
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Full Name *</label>
          <Input
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
            placeholder="Guest's full name"
            data-testid="input-guest-fullname"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1234567890"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">ID Type</label>
            <Select
              value={formData.id_type}
              onValueChange={(value) => setFormData({ ...formData, id_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select ID type" />
              </SelectTrigger>
              <SelectContent>
                {ID_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">ID Number</label>
            <Input
              value={formData.id_number}
              onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
              placeholder="ID/Passport number"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Company/Organization</label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Company name"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Purpose</label>
            <Select
              value={formData.purpose}
              onValueChange={(value) => setFormData({ ...formData, purpose: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GUEST_PURPOSES.map((purpose) => (
                  <SelectItem key={purpose.value} value={purpose.value}>
                    {purpose.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-medium mb-2">Emergency Contact</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500">Contact Name</label>
              <Input
                value={formData.emergency_contact}
                onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                placeholder="Emergency contact name"
              />
            </div>
            <div>
              <label className="text-sm text-gray-500">Contact Phone</label>
              <Input
                value={formData.emergency_phone}
                onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                placeholder="Emergency phone"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this guest"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-guest-btn">
            {isLoading ? 'Saving...' : guest ? 'Update Guest' : 'Create Guest'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="guests-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Guest Directory</h2>
          <p className="text-sm text-gray-500">Manage guest profiles and contact information</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-guest-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Guest
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by name, email, phone, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading guests...</div>
      ) : guests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? 'No guests found' : 'No guests yet'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchTerm 
                ? 'Try a different search term'
                : 'Add your first guest to build your directory'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Guest
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {guests.map((guest) => (
            <Card 
              key={guest.id} 
              className="cursor-pointer hover:shadow-md"
              onClick={() => setEditingGuest(guest)}
              data-testid={`guest-card-${guest.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{guest.full_name}</h3>
                      <Badge variant="outline" className="text-xs capitalize">
                        {guest.purpose}
                      </Badge>
                    </div>
                  </div>
                  {guest.total_stays > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {guest.total_stays} stay{guest.total_stays > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                <div className="space-y-1 text-sm text-gray-500">
                  {guest.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{guest.email}</span>
                    </div>
                  )}
                  {guest.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      <span>{guest.phone}</span>
                    </div>
                  )}
                  {guest.company && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3 w-3" />
                      <span>{guest.company}</span>
                    </div>
                  )}
                  {guest.id_type && guest.id_number && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-3 w-3" />
                      <span className="capitalize">{guest.id_type}: {guest.id_number}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Added {new Date(guest.created_at).toLocaleDateString()}
                  </span>
                  <Button size="sm" variant="ghost" onClick={(e) => {
                    e.stopPropagation();
                    setEditingGuest(guest);
                  }}>
                    <Edit2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Guest Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Guest</DialogTitle>
          </DialogHeader>
          <GuestForm
            onSubmit={(data) => createMutation.mutate(data)}
            onCancel={() => setShowAddDialog(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Guest Dialog */}
      <Dialog open={!!editingGuest} onOpenChange={(open) => !open && setEditingGuest(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Guest</DialogTitle>
          </DialogHeader>
          {editingGuest && (
            <GuestForm
              guest={editingGuest}
              onSubmit={(data) => updateMutation.mutate({ id: editingGuest.id, data })}
              onCancel={() => setEditingGuest(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestsManagement;
